'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2CommentResponse, PrV2ReviewThread } from '@/lib/pr-v2/types';
import { useMemo } from 'react';
import { asRecord } from '@/lib/pr-v2/utils/as-record';
import { useAuth } from '@/providers/auth-provider';
function getNodeId(comment: PrV2CommentResponse): string | null {
  const raw = asRecord(comment.raw);
  return raw && typeof raw.node_id === 'string' ? raw.node_id : null;
}
function toMillis(value: string | null): number {
  if (!value) {
    return 0;
  }
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}
function getCurrentUserLogin(user: ReturnType<typeof useAuth>['user']): string | null {
  if (!user?.user_metadata || typeof user.user_metadata !== 'object') {
    return null;
  }
  const metadata = user.user_metadata as Record<string, unknown>;
  const values = [metadata.user_name, metadata.preferred_username, metadata.login, metadata.name];
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim().toLowerCase();
    }
  }
  return null;
}
export function usePrV2UnresolvedThreadCounts(reviewThreads, comments) {
  const $ = _c(6);
  const {
    user
  } = useAuth();
  let t0;
  if ($[0] !== user) {
    t0 = getCurrentUserLogin(user);
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const currentUserLogin = t0;
  let countsByFilename;
  if ($[2] !== comments || $[3] !== currentUserLogin || $[4] !== reviewThreads) {
    countsByFilename = new Map();
    const commentsByNodeId = new Map();
    comments.forEach(comment => {
      const nodeId = getNodeId(comment);
      if (nodeId) {
        commentsByNodeId.set(nodeId, comment);
      }
    });
    reviewThreads.forEach(thread => {
      if (thread.isResolved || thread.isOutdated) {
        return;
      }
      const mappedComments = thread.comments.nodes.map(node => commentsByNodeId.get(node.id)).filter(_temp);
      const latestComment = mappedComments.slice().sort(_temp2)[0];
      if (latestComment && currentUserLogin && latestComment.userLogin?.toLowerCase() === currentUserLogin) {
        return;
      }
      const path = thread.path ?? mappedComments[0]?.path;
      if (!path) {
        return;
      }
      countsByFilename.set(path, (countsByFilename.get(path) ?? 0) + 1);
    });
    $[2] = comments;
    $[3] = currentUserLogin;
    $[4] = reviewThreads;
    $[5] = countsByFilename;
  } else {
    countsByFilename = $[5];
  }
  return countsByFilename;
}
function _temp2(a, b) {
  return toMillis(b.createdAtGithub ?? b.updatedAtGithub) - toMillis(a.createdAtGithub ?? a.updatedAtGithub);
}
function _temp(comment_0) {
  return Boolean(comment_0);
}